import { forcedStallKind } from '../features/support/devFaults';
import { defineStoredJson } from '../utils/storage';

/**
 * Telling a SLOW database open apart from a BLOCKED one.
 *
 * Both look the same from the app: `createRxDatabase`/`addCollections` simply
 * never comes back. They need opposite advice, though, which is why the old
 * screen sent people to the reset button that cannot help here:
 *
 * - SLOW is a first start or a migration grinding through sixteen collections.
 *   Waiting is right; the app starts by itself.
 * - BLOCKED is a document that was navigated away WHILE it was opening. The
 *   connection it left behind belongs to no living page, and IndexedDB hands it
 *   to nobody else: every later document of this app — this tab, another tab,
 *   after a reload, after a wipe — waits forever on the same database name.
 *   Measured in Chrome 141: a reload does not help, `deleteDatabase` does not
 *   even answer, and only CLOSING the window releases it. The data survives.
 *
 * The difference is measurable: IndexedDB as a whole still works in the blocked
 * case, only that one database is unreachable. So we open a scratch database of
 * our own — if that answers while the real open is still pending, the storage is
 * healthy and ours is being held by a connection nobody can reach.
 */

/** The RxDB database name — here rather than in database.ts, which imports it. */
const BASE_DATABASE_NAME = 'ast_data_app';

/**
 * Which copy of the local database this device is on.
 *
 * A held database cannot be freed from inside the page — measured against every
 * way a page has of trying: reload, a hop through about:blank, a hop through
 * another origin, even `Clear-Site-Data: "executionContexts"`. Only the tab
 * dying releases it, and a kiosk tablet has nobody to close a tab.
 *
 * So the app stops trying to open that name and takes the next one. Everything
 * the server has comes back by syncing; what had not been uploaded stays in the
 * parked copy, which is still READABLE (only the one held name is not) — the
 * rescue in features/support/salvage.ts reads every generation for exactly that
 * reason.
 */
const generation = defineStoredJson<number>({
    key: 'db_generation',
    area: 'local',
    fallback: () => 1,
    decode: (parsed) =>
        typeof parsed === 'number' && parsed >= 1
            ? Math.floor(parsed)
            : undefined,
    keepOnWipe: true
});

/**
 * How many copies this device may give up on before the app stops offering it.
 * Every generation is a full re-sync and leaves the previous one on disk, so a
 * device that keeps landing here has a problem no further copy will fix —
 * better to leave the screen up than to churn through generations unattended.
 */
export const MAX_DATABASE_GENERATIONS = 3;

/** `ast_data_app`, then `ast_data_app_2`, `ast_data_app_3`, … */
export function currentDatabaseName(): string {
    const current = generation.read();
    return current > 1
        ? `${BASE_DATABASE_NAME}_${current}`
        : BASE_DATABASE_NAME;
}

/**
 * Give up on the held copy and start the next one. The caller reloads: the new
 * document opens a database name nothing holds, and the app comes back on its
 * own — no tab to close, no data deleted.
 */
export function startNextDatabaseGeneration(): void {
    generation.write(generation.read() + 1);
    clearOpenAbandoned();
}

/** Whether giving up on this copy is still an answer worth offering. */
export function canStartNextGeneration(): boolean {
    return generation.read() < MAX_DATABASE_GENERATIONS;
}

/** Matches every generation's raw Dexie databases, parked ones included. */
export const RAW_DATABASE_PREFIX = new RegExp(
    `^rxdb-dexie-${BASE_DATABASE_NAME}(_\\d+)?--`
);

/**
 * RxDB's own bookkeeping store, by the name its Dexie storage gives it. Reading
 * a foreign library's naming is a liberty, and a safe one: get it wrong and the
 * probe opens a database nobody uses, which answers at once and lands on the
 * harmless verdict ("slow"). Get it right and it is the sharpest signal there
 * is — this is the exact database a wedged connection holds.
 */
function internalStoreName(): string {
    return `rxdb-dexie-${currentDatabaseName()}--0--_rxdb_internal`;
}

/** Scratch database, opened only to prove IndexedDB itself still answers. */
const PROBE_NAME = 'ast_open_probe';

/** Long enough for a busy device, short enough to keep the screen honest. */
const PROBE_TIMEOUT_MS = 3000;

export type StallKind = 'slow' | 'blocked';

/**
 * A start-up that was interrupted mid-open, remembered across documents — it is
 * the NEXT document that suffers, so this cannot live in sessionStorage. Kept
 * with a timestamp: an hour-old note says nothing about this start.
 */
const abandonedOpen = defineStoredJson<number | null>({
    key: 'db_open_abandoned',
    area: 'local',
    fallback: () => null,
    decode: (parsed) => (typeof parsed === 'number' ? parsed : undefined)
});

/** How long an interrupted open stays a plausible explanation. */
const ABANDONED_FRESH_MS = 10 * 60 * 1000;

/** Note that this document is leaving with its database open still running. */
export function markOpenAbandoned(): void {
    abandonedOpen.write(Date.now());
}

/** The open came through — nothing was left behind. */
export function clearOpenAbandoned(): void {
    abandonedOpen.clear();
}

/** Whether a recent start was interrupted while the database was opening. */
export function hasFreshAbandonedOpen(): boolean {
    const at = abandonedOpen.read();
    if (at === null) {
        return false;
    }
    return Date.now() - at < ABANDONED_FRESH_MS;
}

/**
 * Open `name` and say whether IndexedDB answered at all — for a database that
 * does not exist, creating it counts as an answer. The connection is closed
 * again right away: a probe that stays open would be the next start's problem.
 */
function probeDatabase(name: string): Promise<boolean> {
    if (typeof indexedDB === 'undefined') {
        return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (answered: boolean) => {
            if (!settled) {
                settled = true;
                resolve(answered);
            }
        };
        const timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);
        const done = (answered: boolean) => {
            clearTimeout(timer);
            finish(answered);
        };
        try {
            const request = indexedDB.open(name);
            request.onsuccess = () => {
                request.result.close();
                done(true);
            };
            request.onupgradeneeded = () => {
                // It answered, which is all this asks — but it must not leave a
                // database behind that nobody wanted.
                request.transaction?.abort();
                done(true);
            };
            request.onerror = () => done(false);
            request.onblocked = () => done(false);
        } catch {
            done(false);
        }
    });
}

/**
 * What kind of stall this is — two probes, because one alone lies:
 *
 * - Storage silent: the device is busy or IndexedDB is broken. Either way the
 *   open may still come through, so: slow.
 * - Storage answers AND RxDB's own store answers: the database is reachable and
 *   something merely takes long — a migration grinding through the collections.
 *   Waiting is right; the app finishes by itself. So: slow.
 * - Storage answers while RxDB's store stays silent: that database, and only
 *   that one, is held by a connection nothing can reach any more. Waiting and
 *   resetting are both useless here; closing the window is not. So: blocked.
 */
export async function classifyStall(): Promise<StallKind> {
    // Development only, and compiled away in a build (see devFaults).
    const staged = forcedStallKind();
    if (staged) {
        return staged;
    }
    if (!(await probeDatabase(PROBE_NAME))) {
        return 'slow';
    }
    return (await probeDatabase(internalStoreName())) ? 'slow' : 'blocked';
}
